import { useRef, useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Header.module.css';
import CurrentCityBadge from '../city/CurrentCityBadge';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { logoutUser } from '../../services/firebase';

interface HeaderProps { onNeedUsername?: () => void }
function Header({ onNeedUsername }: HeaderProps) {
  const { currentTheme } = useTheme();
  const { isLoggedIn, userProfile, isPremium } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const avatarBtnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  // Logo yolunu premium durumu ve temaya göre belirle
  const getLogoSrc = () => {
    if (isPremium) {
      // Kullanıcı premium ise
      return currentTheme === 'dark' ? './logo-white-premium.png' : './logo-premium.png';
    } else {
      // Premium değilse
      return currentTheme === 'dark' ? './logo-white.png' : './logo.png';
    }
  };
  
  const logoSrc = getLogoSrc();
  
  // Dış tıklama kapatma
  useEffect(()=>{
    const onDocClick = (e: MouseEvent) => {
      if (!open) return;
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && avatarBtnRef.current && !avatarBtnRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return ()=> document.removeEventListener('mousedown', onDocClick);
  }, [open]);
  
  // Çıkış yapma işlevi
  const toggleMenu = () => {
    // Misafir kullanıcı: direkt aç
    if (!isLoggedIn) { setOpen(o=>!o); return; }
    // Girişli ama username eksikse (belki artık yok) yönlendirici callback
    if (!userProfile?.username) { onNeedUsername?.(); return; }
    setOpen(o=>!o);
  };

  const goProfile = useCallback(()=>{
    if (!userProfile?.username) return; setOpen(false); navigate(`/profile/${userProfile.username}`); }, [navigate, userProfile]);
  const goSettings = useCallback(()=>{ setOpen(false); navigate('/settings'); }, [navigate]);
  const handleLogout = useCallback(async ()=>{ try { await logoutUser(); setOpen(false); navigate('/'); } catch(e){ /* eslint-disable no-console */ console.error(e); } }, [navigate]);

  return (
    <header className={styles.header}>
      <div className={styles.leftSection}>
        <Link to="/" className={styles.logoContainer}>
          <img src={logoSrc} alt="vagonla Logo" className={styles.logoImage} />
        </Link>
        <CurrentCityBadge />
      </div>
      
      <div className={styles.rightSection}>
        <div className={styles.userMenuContainer} ref={menuRef}>
          <button
            ref={avatarBtnRef}
            className={styles.userProfile}
            data-glow
            onClick={toggleMenu}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={isLoggedIn ? 'Profil menüsü' : 'Misafir menüsü'}
            type="button"
          >
            {isLoggedIn ? (
              userProfile?.photoURL ? (
                <img src={userProfile.photoURL} alt={userProfile.displayName || 'Profil'} className={styles.userAvatar} referrerPolicy="no-referrer" />
              ) : (
                <div className={styles.fallbackAvatar} aria-hidden="true">{(userProfile?.displayName || 'K').charAt(0).toUpperCase()}</div>
              )
            ) : (
              <div className={styles.emptyAvatar} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5Z" />
                  <path d="M3.5 21c1.4-3.5 4.6-6 8.5-6s7.1 2.5 8.5 6" />
                </svg>
              </div>
            )}
          </button>
          {open && (
            <div className={styles.dropdownMenu} role="menu">
              {isLoggedIn && (
                <div className={styles.dropdownHeader}>
                  <div className={styles.dropdownName}>{userProfile?.displayName}</div>
                  {userProfile?.username && <div className={styles.dropdownUsername}>@{userProfile.username}</div>}
                </div>
              )}
              <div className={styles.dropdownItems}>
                {isLoggedIn ? (
                  <>
                    <button type="button" className={styles.dropdownItem} onClick={goProfile}>Profil</button>
                    <button type="button" className={styles.dropdownItem} onClick={goSettings}>Ayarlar</button>
                    <div className={styles.dropdownDivider} />
                    <button type="button" className={styles.dropdownItemDanger} onClick={handleLogout}>Çıkış Yap</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className={styles.dropdownItem} onClick={() => setOpen(false)}>Giriş Yap</Link>
                    <Link to="/register" className={styles.dropdownItem} onClick={() => setOpen(false)}>Kayıt Ol</Link>
                    <div className={styles.dropdownDivider} />
                    <button type="button" className={styles.dropdownItem} onClick={() => { setOpen(false); navigate('/settings'); }}>Ayarlar</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
